import { useAuthContext } from '@cognite/e2e-auth';
import type { AppConfig } from '@infield/features/app-config/types';
import { LOCIZE_NAMESPACES, useTranslation } from '@infield/features/i18n';
import { QueryKeys } from '@infield/utils/queryKeys';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';

import { UseUpsertAppConfigMutationContext } from './use-upsert-app-config-mutation.context';

export const useUpsertAppConfigMutation = ({
  showToast = true,
}: {
  showToast?: boolean;
} = {}) => {
  const coreAuth = useAuthContext();
  const queryClient = useQueryClient();
  const { t } = useTranslation(LOCIZE_NAMESPACES.configuration);
  const { getAppConfigClient, makeToast, captureException } = useContext(
    UseUpsertAppConfigMutationContext
  );

  return useMutation<
    void,
    Error,
    { newAppConfig: Partial<AppConfig> & { externalId: string } }
  >(
    async ({ newAppConfig }) => {
      const schemaVersionOverride =
        coreAuth.authState?.status === 'AUTHENTICATED'
          ? coreAuth.authState.projectInfo?.configurationSchemaVersion
          : undefined;

      const appConfigClient = getAppConfigClient(
        coreAuth.client,
        schemaVersionOverride
      );

      const nextConfig: Partial<AppConfig> & { externalId: string } = {
        externalId: newAppConfig.externalId,
        customerDataSpaceId: newAppConfig.customerDataSpaceId,
        customerDataSpaceVersion: newAppConfig.customerDataSpaceVersion,
        appDataSpaceId: newAppConfig.appDataSpaceId,
        appDataSpaceVersion: newAppConfig.appDataSpaceVersion,
        featureConfiguration: newAppConfig.featureConfiguration,
      };

      await appConfigClient.upsertConfiguration(nextConfig);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.APP_CONFIG],
        });
      },
      onError: (err) => {
        if (showToast) {
          makeToast({
            body: t(
              'INFIELD_APP_CONFIG_UPSERT_ERROR',
              'Failed to update application configuration'
            ),
            type: 'danger',
          });
        }
        captureException(err);
      },
    }
  );
};
